import { Box, useTheme } from '@mui/material';

import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { PageMetaDataModel } from '@/logic/services/seo';
import Seo from '../seo/seo';
import { dynamicPageResponse } from '@/logic/services/dynamic-pages';
import { pxToRem } from '@/theme/typography';
import { MovieLoaderWrapper } from './movie-loader-wrapper';

interface DynamicPageProps {
  title: string;
  path: string;
  seoPage: PageMetaDataModel;
  isLoading: boolean;
  pageData: dynamicPageResponse;
}

export const DynamicPageComponent: FC<DynamicPageProps> = ({
  title,
  path,
  seoPage,
  isLoading,
  pageData,
}) => {
  const theme = useTheme();

  return (
    <>
      <Seo
        title={seoPage?.titleTag || title}
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag}
        image={pageData?.photoUrl}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      <Box
        component='h1'
        sx={{
          fontSize: pxToRem(30),
          ml: pxToRem(128),
          py: pxToRem(52),
          [theme.breakpoints.down(1350)]: {
            ml: pxToRem(16),
          },
          [theme.breakpoints.down('tablet')]: {
            fontSize: pxToRem(18),
            pt: pxToRem(32),
            pb: pxToRem(24),
          },
        }}
      >
        <FormattedMessage id={title} />
      </Box>

      <Box
        component='section'
        sx={{
          marginBlockEnd: 5,
          background: theme.palette.layout.bgSecondary,
          maxWidth: '1102px',
          mx: 'auto',
          px: pxToRem(140),
          pb: pxToRem(51),
          mb: pxToRem(23),

          borderRadius: pxToRem(25),
          fontSize: pxToRem(24),

          [theme.breakpoints.down(1134)]: {
            m: pxToRem(16),
            mt: 0,
          },
          [theme.breakpoints.down('tablet')]: {
            fontSize: pxToRem(16),
            p: pxToRem(16),
            mb: pxToRem(70),
          },
        }}
      >
        {!isLoading ? (
          <>
            <Box
              component='div'
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                component='img'
                src={pageData?.photoUrl}
                alt='about-us'
                sx={{
                  width: pxToRem(200),
                  height: pxToRem(200),
                  objectFit: 'contain',
                  mt: pxToRem(44),
                  mb: pxToRem(76),
                  mx: 'auto',
                  [theme.breakpoints.down('tablet')]: {
                    width: pxToRem(83),
                    height: pxToRem(96),
                    my: pxToRem(24),
                  },
                }}
              />
            </Box>

            <Box
              component='div'
              dangerouslySetInnerHTML={{ __html: pageData?.content as string }}
              sx={{
                [theme.breakpoints.down('tablet')]: {
                  fontSize: '16px !important',
                  '& span,p,a,q,div,b ,strong ,i ,em ,mark ,small,del,ins ,sub,sup': {
                    fontSize: '16px !important',
                  },
                  '& h1 ,h2': {
                    fontSize: '18px !important',
                    '& span': {
                      fontSize: '18px !important',
                    },
                  },
                  '& h3 ,h4': {
                    fontSize: '17px !important',
                    '& span': {
                      fontSize: '17px !important',
                    },
                  },
                },
              }}
            />
          </>
        ) : (
          <MovieLoaderWrapper
            innerBox={{ width: '200px', height: '200px' }}
            outerBox={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '40vh',
            }}
          />
        )}
      </Box>
    </>
  );
};

export default DynamicPageComponent;
